import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { LnrpService } from '../../services/Lnrp.service';

export interface LnrpState {
  loggedIn: boolean;
  loading: boolean;
  error?: string;
  downloadPath: string | null;
}

const getDownloadPathFromLocalStorage = (): string | null => {
  const downloadPath = localStorage.getItem('lnrpDownloadPath');
  return downloadPath || null;
};

const initialState: LnrpState = {
  loggedIn: false,
  loading: false,
  error: undefined,
  downloadPath: getDownloadPathFromLocalStorage(),
};

export const checkLnrpStatus = createAsyncThunk<
  boolean,
  void,
  { rejectValue: string }
>('lnrp/checkStatus', async (_, { rejectWithValue }) => {
  try {
    const resp = await LnrpService.lnrpStatus();
    return resp.loggedIn;
  } catch {
    return rejectWithValue('Failed to check LNRP status');
  }
});

export const lnrpLogin = createAsyncThunk<
  boolean,
  { username: string; password: string },
  { rejectValue: string }
>('lnrp/login', async ({ username, password }, { rejectWithValue }) => {
  try {
    const loginResult = await LnrpService.lnrpLogin(username, password);
    return loginResult?.status === 'active';
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.message || 'Login failed');
  }
});

export const lnrpLogout = createAsyncThunk<void, void, { rejectValue: string }>(
  'lnrp/logout',
  async (_, { rejectWithValue }) => {
    try {
      await LnrpService.lnrpLogout();
    } catch {
      return rejectWithValue('Logout failed');
    }
  },
);

const lnrpSlice = createSlice({
  name: 'lnrp',
  initialState,
  reducers: {
    setLNRPDownloadPath(state, action: PayloadAction<string>) {
      state.downloadPath = action.payload;
      localStorage.setItem('lnrpDownloadPath', action.payload);
    },
    clearLNRPDownloadPath(state) {
      state.downloadPath = null;
      localStorage.removeItem('lnrpDownloadPath');
    },
  },
  extraReducers: (builder) => {
    // checkStatus
    builder
      .addCase(checkLnrpStatus.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(checkLnrpStatus.fulfilled, (state, action) => {
        state.loading = false;
        state.loggedIn = action.payload;
      })
      .addCase(checkLnrpStatus.rejected, (state, action) => {
        state.loading = false;
        state.loggedIn = false;
        state.error = action.payload;
      });

    // login
    builder
      .addCase(lnrpLogin.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(lnrpLogin.fulfilled, (state) => {
        state.loading = false;
        state.loggedIn = true;
      })
      .addCase(lnrpLogin.rejected, (state, action) => {
        state.loading = false;
        state.loggedIn = false;
        state.error = action.payload;
      });

    // logout
    builder
      .addCase(lnrpLogout.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(lnrpLogout.fulfilled, (state) => {
        state.loading = false;
        state.loggedIn = false;
      })
      .addCase(lnrpLogout.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { setLNRPDownloadPath, clearLNRPDownloadPath } = lnrpSlice.actions;
export default lnrpSlice.reducer;

// Selectors:
export const selectLnrpLoggedIn = (state: any) => state.lnrp.loggedIn;
export const selectLnrpLoading = (state: any) => state.lnrp.loading;
export const selectLnrpError = (state: any) => state.lnrp.error;
export const selectLnrpDownloadPath = (state: any) => state.lnrp.downloadPath;
